<template>
    <vx-chart-record
        :loading="loading"
        :error="Boolean(error)"
        :percentage="true"
        :data="dataComputed || []"
        @item:click="($event, item) => emit('resource:click', $event, item)"
    >
        <template v-slot:title>
            <slot name="title"> {{ resource.title }} per Resource </slot>
            &rarr; Top {{ dataComputed?.length || 10 }}
        </template>
        <template v-slot:subtitle>
            <slot name="subtitle"></slot>
        </template>

        <template v-slot:item-title="{ item }">
            <resource-details-popover
                v-if="shouldShowResourceDetails(item)"
                class="[&>div:first-child]:truncate w-full"
                :teamId="teamId"
                :orgUnitId="props.orgUnitId"
                :groupId="props.groupId"
                :modelValue="{
                    id: item.resource_id,
                    name: item.resource_name,
                    resource_type: item.resource_type,
                }"
                nameIcon
                hideSendButton
            >
                {{ item.title }}
            </resource-details-popover>
            <vx-popover v-else hover placement="top" arrow class="[&>div:first-child]:truncate w-full">
                {{ item.title }}
                <template v-slot:content>
                    <div class="flex items-center max-w-lg overflow-hidden">
                        <font-awesome-icon
                            widthAuto
                            :icon="resourceTypeIcon(item.resource_type)"
                            class="mr-2 text-slate-500"
                        ></font-awesome-icon>
                        <span class="font-bold text-slate-700 text-lg break-all">{{ item.title }}</span>
                        <vx-badge size="xs" color="slate" class="ml-2">{{ item.resource_id }}</vx-badge>
                    </div>
                </template>
            </vx-popover>
        </template>

        <template v-slot:item-icons="{ item }">
            <font-awesome-icon
                v-if="FEATURES.contact_groups && item.sender_type"
                fixedWidth
                :icon="getSenderTypeIcon(item.sender_type, 'regular')"
                :class="getSenderTypeColorClass(item.sender_type)"
            ></font-awesome-icon>
            <font-awesome-icon fixedWidth :icon="resourceTypeIcon(item.resource_type, 'regular')"></font-awesome-icon>
        </template>

        <template v-slot:explainHeader>{{ resource.title }} per Resource</template>
        <template v-slot:explainContent>
            <p>
                <strong>{{ resource.explainTitle }}</strong
                ><br />
                {{ resource.explainContent }}
            </p>
        </template>
    </vx-chart-record>
</template>

<script setup>
import { VxChartRecord, VxPopover, VxBadge } from '@voxie/frontend-components';
import { outboundMessagesAnalyticsClient } from '~/services';
import { inject, watch, computed } from 'vue';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import {
    metricsInputValidator,
    resourceTypeIcon,
    metricsWithGoals,
    shouldShowResourceDetails,
    getSenderTypeIcon,
    getSenderTypeColorClass,
} from '~/utils/analytics';
import { stringifyQueryString } from '~/utils/string';
import ResourceDetailsPopover from '~/components/general/resources/ResourceDetailsPopover.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const FEATURES = inject('FEATURES');

const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    rateType: {
        type: String,
        validator: (resourceType) => metricsWithGoals.includes(resourceType),
        required: true,
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
});

const emit = defineEmits(['resource:click']);

const teamId = Spark.state.currentTeam.id;

const context = computed(() => ({
    teamId,
    orgUnitId: props.orgUnitId,
    groupId: props.groupId,
}));

const resource = computed(() => {
    switch (props.rateType) {
        case 'unique_click_through_rate':
            return {
                title: 'Unique Click-Through Rate',
                explainTitle: 'Unique Click-Through Rate',
                explainContent:
                    'Most relevant Voxie Resources, in particular the ones that provide a clickable link; this metric allows you to evaluate the success through the unique clicks a Message Resource receives relative to the quantity of messages delivered.',
                resourceDataRequest: outboundMessagesAnalyticsClient.getUniqueClickThroughRateByResource(
                    context.value,
                    props.fetchParams
                ),
            };
        case 'click_through_rate':
            return {
                title: 'Click-Through Rate',
                explainTitle: 'Click-Through Rate',
                explainContent:
                    'Most relevant Voxie Resources, in particular the ones that provide a clickable link; this metric allows you to evaluate the success through the relative clicks a Message Resource receives relative to the quantity of messages delivered.',
                resourceDataRequest: outboundMessagesAnalyticsClient.getClickThroughRateByResource(
                    context.value,
                    props.fetchParams
                ),
            };
        case 'message_reply_rate':
            return {
                title: 'Message Replies',
                explainTitle: 'Message Replies',
                explainContent:
                    'Most relevant Voxie Resources in terms of reply percentage of the messages delivered relative to the messages delivered during the selected period.',
                resourceDataRequest: outboundMessagesAnalyticsClient.getReplyRateByResource(
                    context.value,
                    props.fetchParams
                ),
            };
        case 'opt_out_rate':
            return {
                title: 'Unsubscribe',
                explainTitle: 'Unsubscribers %',
                explainContent:
                    'Most relevant Voxie Resources in terms of opt-out percentage relative to the messages delivered during the selected period.',
                resourceDataRequest: outboundMessagesAnalyticsClient.getOptOutRateByResource(
                    context.value,
                    props.fetchParams
                ),
            };
        default:
            return {
                title: 'Unknown',
                explainTitle: 'Unknown',
                explainContent: 'unknown',
                resourceDataRequest: async () => ({ data: { data: [] } }),
            };
    }
});

const { loading, response, error, fetchData } = useVersionedRequest();

const dataComputed = computed(() => {
    return (response.value?.data?.data || []).map((item) => {
        const queryString = {
            ...props.fetchParams,
            filters: {
                ...props.fetchParams.filters,
                resources: [
                    {
                        resource_type: item.resource_type,
                        resource_id: item.resource_id,
                    },
                ],
            },
        };
        return {
            ...item,
            href: '/analytics/outbound-messages?' + stringifyQueryString(queryString),
            key: item.resource_id,
            title: item.resource_name,
            value: item.values[0],
        };
    });
});

watch(
    () => props.fetchParams,
    () => {
        fetchData(resource.value.resourceDataRequest).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true, deep: true }
);
</script>
